/**
 * The lesson engine's vocabulary. Every Tigard lesson, K–8, is built from these pieces.
 *
 * The rule under all of it (Meliesa, Oct 28–29): pattern first, the rule is the student's own discovery, never a
 * procedure printed ahead of the pattern. So a lesson is:
 *   discover → a set-up and a sequence of questions; the student answers each one; the book's sentence is revealed
 *              only AFTER the student has found it ("reveal").
 *   practice → generated problems of one skill until the student shows mastery; hints never give the answer.
 *   tables   → a times-tables unit from the Grade 3 Times Tables app, run inside the lesson.
 * A wrong answer gets the matching response for that mistake if there is one (the misconception library — this is
 * where Meliesa's teaching lives), otherwise the next rung of the hint ladder. After the last rung the student is shown
 * the way (never just the number) and gets a fresh problem of the same kind.
 */

export type InputSpec =
  | { kind: "number"; label?: string; width?: number }
  | { kind: "choice"; options: string[] }
  | { kind: "text"; label?: string }
  /** Tap squares on the Math Map ("circle every nine"). The answer is the set, comma-joined. */
  /** A fraction: top box over bottom box. The answer is "top/bottom". */
  | { kind: "fraction" }
  /** Several boxes in a row, e.g. hours and minutes. The answer is the values joined with "|". */
  | { kind: "fields"; fields: { label?: string; suffix?: string; width?: number; text?: boolean }[] }
  /** Tap parts of a circle or bar to shade them. The answer is how many are shaded. */
  | { kind: "shade"; parts: number; shape: "pie" | "bar" }
  /** Pick `need` of the options (authored HTML, e.g. shapes). The answer is the picked indexes, ascending, joined with ",". */
  | { kind: "multi"; options: string[]; need: number; label?: string; /** "Circle exactly N": any N count — the value is how many are picked. */ count?: boolean }
  /** Column work: ones first, trade boxes / "go get a ten" (src/ui/vertical.ts). */
  | { kind: "vertical"; top: number; bottom: number; op: "+" | "−" | "×" }
  /** A list of whole numbers in one box ("1, 2, 3, 6"). The answer is the numbers in the order typed, joined with ";". */
  | { kind: "list"; label?: string }
  /** Tap points on a first-quadrant grid (0…max). The answer is the points "x:y", sorted, joined with ";". */
  | { kind: "coord"; max: number; maxY?: number; min?: number }
  /** Tap a tick on a 0-to-1 number line cut into `parts` equal hops. The answer is the tick's hop count (0…parts). */
  | { kind: "plot"; parts: number }
  /** Graph an inequality without drawing: pick the sign, tap the boundary, pick the dot (when ≤/≥ are in play) and the arrow. Value "lt|6|open|left". */
  | { kind: "ineq"; min: number; max: number; filled?: boolean; letter?: string }
  /** The function game: the student says numbers, the computer applies y = a·x + b (or x ÷ div + b), then the student writes the rule. */
  | { kind: "fngame"; a: number; b: number; div?: number }
  /** A grid of boxes, some given, some to fill (the fives puzzle, the twos grid, the tens cross, count-by strips).
   *  cells: a string is given, null is a box to fill, "" is no box at all. The answer is the filled boxes in order, "|"-joined. */
  | { kind: "grid"; cols: number; cells: (string | null)[]; head?: number; hl?: number[] }
  /** Build a number with base-ten blocks: add or take away flats, rods, cubes. The answer is "hundreds|tens|ones". */
  | { kind: "blocks" }
  /** Set the hands on a clock: the hour hand moves a minute-mark at a time, the minute hand five minutes at a time.
   *  The answer is "hourMark|minuteMark" (0–59, 0–11). */
  | { kind: "clockset" }
  | { kind: "tap"; rows: number; targets: number[]; start?: number[]; wrongTap?: (n: number, found: number[]) => string };

export interface Misconception {
  /** Short stable code for the research data and the teacher view, e.g. "round-wrong-place". */
  code: string;
  /** What Meliesa would say. Never the answer. */
  hint: string;
}

export interface Item {
  /** Stable id for logging: skill + parameters, e.g. "round-10:548". */
  id: string;
  skill: string;
  /** The question. Authored HTML (trusted, never user input). */
  prompt: string;
  /** Picture, map, puzzle — authored HTML. */
  visual?: string;
  input: InputSpec;
  /** Canonical answer, as the student would type or choose it. */
  answer: string;
  /** Other forms that count as right ("5 o'clock" for "5:00"). Compared after normalizing. */
  alsoAccept?: string[];
  /** A specific wrong answer → Meliesa's response to it. */
  diagnose?: (raw: string) => Misconception | null;
  /** The hint ladder, gentlest first. The last rung shows the way (the method on this problem), not the number. */
  hints: string[];
  /** Said after a correct answer — in a discovery step this is the book's sentence the student just earned. */
  reveal?: string;
  /** Read-aloud text if it differs from the prompt's text content. */
  speak?: string;
}

/** A generator for one skill: `level` lets a practice block start easy and climb. */
export type Generator = (rng: () => number, level: number) => Item;

export interface Skill {
  id: string;
  name: string;
  gen: Generator;
}

export interface MasteryRule {
  /** Fewest problems before mastery can be shown. A fast student can finish here. */
  min: number;
  /** Look at the last `window` first-try results… */
  window: number;
  /** …and need this many right. */
  need: number;
  /** After this many problems without mastery, the block ends and the teacher is told (the student is never trapped). */
  max: number;
}

export const DEFAULT_MASTERY: MasteryRule = { min: 5, window: 6, need: 5, max: 24 };

/** A check-up item: no hints, no feedback; scored by domain; tied to the lesson that teaches it. */
export interface CheckItem extends Item {
  domain: "OA" | "NBT" | "NF" | "MD" | "G" | "NS" | "RP" | "EE" | "SP" | "U1" | "U2" | "U3" | "DATA" | "FN";
  /** The Level lesson that teaches it (for placement and re-teaching). */
  lesson: number;
  /** Points on the paper test (default 1) — e.g. "circle the two parallelograms" is worth 2. */
  points?: number;
}

export type Step =
  | { kind: "discover"; id: string; title: string; intro?: string; items: Item[] }
  | { kind: "practice"; id: string; title: string; intro?: string; skill: string; mastery?: Partial<MasteryRule>; backup?: string;
      /** Given first, in order, before any generated problem (the whole 1-digit series). Mastery waits for them. */
      lead?: () => Item[] }
  | { kind: "tables"; id: string; title: string; intro?: string; units: string[] }
  | { kind: "soon"; id: string; title: string; note: string }
  | { kind: "check"; id: string; title: string; intro?: string; items: CheckItem[] }
  /** The placement diagnostic (lessons/placement.ts). */
  | { kind: "placement"; id: string; title: string; intro?: string }
  /** The facts check — the method-neutral pre/post measure (lessons/factsTest.ts). */
  | { kind: "factstest"; id: string; title: string; intro?: string };

export interface Lesson {
  id: string;
  level: string;
  num: number;
  title: string;
  /** One line for the lesson card. */
  blurb: string;
  /** Shown instead of the number when the book doesn't number it (the Kindergarten reviews: "Review 1"). */
  tag?: string;
  steps: Step[];
}
